/**
 * Copyright (C) Firstlight Media. All rights reserved.
 * All information contained herein is and remains the property of Firstlight Media.
 * The intellectual and technical concepts contained herein are proprietary
 * to Firstlight Media. Dissemination of this information or reproduction
 * of this material is strictly forbidden unless prior written permission
 * is obtained from Firstlight Media.
 */

import { ApplicationDataKey } from '../../datakey/ApplicationDataKey';
import { DeviceDataKey } from '../../datakey/DeviceDataKey';
import { UserDataKey } from '../../datakey/UserDataKey';
import { Application } from '../../model/Application';
import { Device } from '../../model/Device';
import { User } from '../../model/User';
import { ApplicationMetrics } from '../../model/ApplicationMetrics';
import { ApplicationMetricsDataKey } from '../../datakey/ApplicationMetricsDataKey';
import { ApplicationError } from '../../model/ApplicationError';
import { ApplicationErrorDataKey } from '../../datakey/ApplicationErrorDataKey';
import { Subscription } from '../../model/Subscription';
import { Content } from '../../model/Content';
import { ContentDataKey } from '../../datakey/ContentDataKey';
import { ApplicationContainer } from '../../model/ApplicationContainer';
import { ApplicationContainerDataKey } from '../../datakey/ApplicationContainerDataKey';
import { UserError } from '../../model/UserError';
import { UserErrorDataKey } from '../../datakey/UserErrorDataKey';
import { TVShow } from '../../model/TVShow';
import { SportContent } from '../../model/SportContent';
import { PlaybackError } from '../../model/PlaybackError';
import { PlaybackErrorDataKey } from '../../datakey/PlaybackErrorDataKey';
import { SportVenue } from '../../model/SportVenue';
import { PlaybackDataKey } from '../../datakey/PlaybackDataKey';
import { Constants as ConvivaConstants } from '@convivainc/conviva-js-coresdk';
import { PLAYER_NAME } from '../../Constants';
import { Error, ErrorCodes, PlatformUtil } from '@quickplay/foundation';
import { UserJourneyDataKey } from '../../datakey/UserJourneyDataKey';
import { Provider } from '../Provider';
import { DrmScheme } from '@quickplay/player-interface';
import { PlatformName, StreamType, version } from '../../fl-analytics';

/**
 * Builds an object with application custom metadata to be reported.
 * @param application
 */
export function toApplicationReportingData(application: Application): { [key: string]: any } {
  return {
    [ApplicationDataKey.APP_NAME]: application.name,
    [ApplicationDataKey.APP_VERSION]: application.version,
    [ApplicationDataKey.APP_BUILD_VERSION]: application.build,
    [ApplicationDataKey.APP_PRODUCT]: application.product,
    [ApplicationDataKey.APP_VERSION_ID]: application.versionId,
    [ApplicationDataKey.DISPLAY_LANGUAGE]: application.displayLanguage,
    [ApplicationDataKey.PROPERTY]: application.property,
    [ApplicationDataKey.STATION]: application.station,
    [ApplicationDataKey.QP_SDK_VERSION]: version,
  };
}

/**
 * Builds an object with user custom metadata to be reported.
 * @param user
 */
export function toUserReportingData(user: User): { [key in UserDataKey]?: any } {
  const baseData: { [key in UserDataKey]?: any } = {
    ...toSubscriptionReportingData(user.subscription),
    [UserDataKey.USER_ID]: user.id,
    [UserDataKey.USER_TYPE]: user.type,
    [UserDataKey.PROFILE_ID]: user.profileId,
  };

  //Note: We should not override the profileType incase of `null` or `undefined`
  if (user.profileType !== undefined && user.profileType !== null) {
    baseData[UserDataKey.PROFILE_TYPE] = user.profileType;
  }

  return baseData;
}

/**
 * Builds an object with user custom metadata to be reported for NPAW.
 * @param user
 */
export function toNPAWUserReportingData(user: User): { [key in any]?: any } {
  return {
    userId: user.id,
    'user.type': user.type,
    'user.profileId': user.profileId,
    'content.transactionCode': user?.transactionCode,
  };
}
/**
 * Builds an object with device custom metadata to be reported.
 * @param device
 */
export function toDeviceReportingData(device: Device): { [key: string]: any } {
  return {
    [DeviceDataKey.PLATFORM_TYPE]: device.platformType,
    [DeviceDataKey.CUSTOM_DEVICE_MANUFACTURER]: device.customDeviceManufacturer,
    [DeviceDataKey.CUSTOM_DEVICE_NAME]: device.customDeviceName,
    [DeviceDataKey.QP_GLOBAL_ID]: device.qpGlobalId,
    [DeviceDataKey.PLATFORM_NAME]: device.platformName,
    [DeviceDataKey.BRAND]: device.brand,
    [DeviceDataKey.CATEGORY]: device.category,
    [DeviceDataKey.MODEL]: device.model,
    [DeviceDataKey.OS_NAME]: device.osName,
    [DeviceDataKey.OS_VERSION]: device.osVersion,
    [DeviceDataKey.SCREEN_RESOLUTION_HEIGHT]: device.screenResolutionHeight,
    [DeviceDataKey.SCREEN_RESOLUTION_WIDTH]: device.screenResolutionWidth,
    [DeviceDataKey.SCREEN_RESOLUTION_SCALE_FACTOR]: device.screenResolutionScaleFactor,
    [DeviceDataKey.TYPE]: device.type,
    [DeviceDataKey.VERSION]: device.version,
  };
}

export function toNewRelicDeviceReportingData(device: Device): { [key: string]: any } {
  return {
    [DeviceDataKey.PLATFORM_NAME]: device.platformName || getCurrentPlatform(),
    [DeviceDataKey.DEVICE_NAME]: device.customDeviceName,
    [DeviceDataKey.DEVICE_GROUP]: device.category,
    [DeviceDataKey.DEVICE_MANUFACTURER]: device.customDeviceManufacturer,
    [DeviceDataKey.MODEL]: device.model,
    [DeviceDataKey.DEVICE_CAPABILITY]: device.deviceCapability,
    [DeviceDataKey.DEVICE_ID]: device.qpGlobalId,
  };
}

export function toNewRelicUserReportingData(user: User): { [key: string]: any } {
  return {
    [UserDataKey.USER_ID]: user.id,
    [UserDataKey.USER_TYPE]: user.type,
    [UserDataKey.PROFILE_ID]: user.profileId,
    [UserDataKey.BASE_SUBSCRIPTION]: user.baseSubscription,
    [UserDataKey.SILENT_LOGIN]: user.silentLogin,
    [UserDataKey.PROFILE_TYPE]: user.profileType,
  };
}

/**
 * Builds an object with application metrics custom metadata to be reported.
 * @param applicationMetrics
 */
export function toApplicationMetricsReportingData(
  applicationMetrics: ApplicationMetrics,
  provider: Provider,
): { [key: string]: any } {
  if (provider === Provider.NEWRELIC) {
    return {
      [ApplicationMetricsDataKey.TIME_TO_APP_START]: applicationMetrics.startupTime,
      [ApplicationMetricsDataKey.TIME_TO_SPLASH]: applicationMetrics.timeToSplash,
      ...applicationMetrics.customMetrics,
    };
  } else {
    return {
      [ApplicationMetricsDataKey.APP_STARTUP_TIME]: applicationMetrics.startupTime,
    };
  }
}

/**
 * Builds an object with application error custom metadata to be reported.
 * @param applicationError
 */
export function toApplicationErrorReportingData(
  applicationError?: ApplicationError,
  provider?: Provider,
): { [key: string]: any } {
  if (!applicationError) {
    return {};
  }

  const errorData = {
    [ApplicationErrorDataKey.APP_ERROR_MESSAGE]: applicationError.message,
    [ApplicationErrorDataKey.APP_ERROR_DESCRIPTION]: applicationError.description,
    [ApplicationErrorDataKey.APP_ERROR_INTERNAL]: applicationError.internalError,
  };

  if (provider === Provider.NEWRELIC) {
    return {
      ...errorData,
      [ApplicationErrorDataKey.APP_ERROR_CODE]: applicationError.code,
      [ApplicationErrorDataKey.APP_ERROR_TYPE]: applicationError.errorType,
      [ApplicationErrorDataKey.APP_ERROR_SEVERITY]: applicationError.severity,
    };
  }

  return { ...errorData, [ApplicationErrorDataKey.APP_ERROR]: applicationError.code };
}

/**
 * Builds an object with playback error custom metadata to be reported.
 * @param playbackError
 */
export function toPlaybackErrorReportingData(
  playbackError: PlaybackError,
  contentMetadata: { [key: string]: any },
): { [key: string]: any } {
  return {
    ...toApplicationContainerReportingData(playbackError.container),
    ...contentMetadata,
    [PlaybackDataKey.PLAYBACK_SOURCE]: playbackError.playbackSource,
    [PlaybackErrorDataKey.PLAYBACK_ERROR_CODE]: playbackError.code,
    [PlaybackErrorDataKey.PLAYBACK_ERROR_MESSAGE]: playbackError.message,
    [PlaybackErrorDataKey.PLAYBACK_ERROR_DESCRIPTION]: playbackError.description,
    [PlaybackErrorDataKey.PLAYBACK_ERROR_INTERNAL]: playbackError.internalError,
  };
}

/**
 * Builds an object with subscription custom metadata to be reported.
 * @param subscription
 */
export function toSubscriptionReportingData(subscription?: Subscription): { [key: string]: any } {
  return {
    [UserDataKey.USER_SUBSCRIPTION_ID]: subscription?.id,
    [UserDataKey.USER_SUBSCRIPTION_STATUS]: subscription?.status,
    [UserDataKey.SUBSCRIPTION_PLAN]: subscription?.plan,
    [UserDataKey.SUBSCRIPTION_PLAN_ID]: subscription?.planId,
  };
}

export function toConvivaReportingData(content: Content): { [key: string]: any } {
  return {
    // Conviva attributes
    [ConvivaConstants.IS_LIVE]: content.isLive,
    [ConvivaConstants.ASSET_NAME]: content.name,
    [ConvivaConstants.DURATION]: content.duration,
    [ConvivaConstants.STREAM_URL]: content.streamUrl,
    [ConvivaConstants.ENCODED_FRAMERATE]: content.encodedFramerate,
    [ConvivaConstants.VIEWER_ID]: content.viewerId,
    [ConvivaConstants.DEFAULT_RESOURCE]: content.defaultResource,
    ['c3.cm.contentType']: content.type,
    ['c3.cm.channel']: content.channel ?? 'NA',
    ['c3.cm.brand']: content.brand ?? 'NA',
    ['c3.cm.affiliate']: content.affiliate ?? 'NA',
    ['c3.cm.categoryType']: content.categoryType,
    ['c3.cm.name']: content.assetProvider,
    ['c3.cm.id']: content.id,
    ['c3.cm.seriesName']: content.seriesName,
    ['c3.cm.seasonNumber']: content.seasonNumber,
    ['c3.cm.showTitle']: content.showTitle,
    ['c3.cm.episodeNumber']: content.episodeNumber,
    ['c3.cm.genre']: content.genre,
    ['c3.cm.genreList']: content.genreList,
    ['c3.cm.utmTrackingUrl']: content.utmUrl ?? 'NA',
    [ContentDataKey.PROVIDER_ID]: content.providerId,
    [ContentDataKey.LICENSE_WINDOW_START_DATE]: content.licenseWindowStartDate,
  };
}

export const toNewRelicContentData = (content: Content): { [key: string]: any } => {
  return {
    [ContentDataKey.CONTENT_ID]: content.id,
    [ContentDataKey.EXTERNAL_CONTENT_ID]: content.externalContentId,
    [ContentDataKey.ENRICHMENT_CONTENT_ID]: content.enrichmentContentId,
    [ContentDataKey.CONTENT_TYPE]: content.type,
    [ContentDataKey.CONTENT_NAME]: content.name,
    [ContentDataKey.PROVIDER_ID]: content.providerId,
    [ContentDataKey.CHANNEL_ID]: content.channelId,
    [ContentDataKey.CHANNEL + '_name']: content.channel,
    [ContentDataKey.PROGRAM_ID]: content.programId,
    [ContentDataKey.PROGRAM_NAME]: content.programName,
    ['content_' + ContentDataKey.GENRE]: content.genre,
    [ContentDataKey.SERIES_ID]: content.seriesId,
    [ContentDataKey.SERIES_NAME]: content.seriesName,
    [ContentDataKey.SEASON_NUMBER]: content.seasonNumber,
    [ContentDataKey.EPISODE_NUMBER]: content.episodeNumber,
    [ContentDataKey.CONTENT_LANGUAGE]: content.contentLanguage,

    [PlaybackDataKey.PLAYBACK_MODE]: content.playbackMode,

    [UserJourneyDataKey.PAGE_ID]: content.pageId,
    [UserJourneyDataKey.STOREFRONT_ID]: content.storefrontId,
    [UserJourneyDataKey.STOREFRONT_NAME]: content.storefrontName,
    [UserJourneyDataKey.TAB_ID]: content.tabId,
    [UserJourneyDataKey.TAB_NAME]: content.tabName,
    [UserJourneyDataKey.CONTAINER_ID]: content.containerId,
    [UserJourneyDataKey.CONTAINER_NAME]: content.containerName,
    [UserJourneyDataKey.COLLECTION_ID]: content.collectionId,
    [UserJourneyDataKey.COLLECTION_NAME]: content.collectionName,
  };
};

/**
 * Builds an object with content custom metadata to be reported.
 * @param content
 */
export function toDatazoomReportingData(content: Content): { [key: string]: any } {
  let contentAttributes = {
    [ContentDataKey.CONTENT_ID]: content.id,
    [ContentDataKey.EXTERNAL_CONTENT_ID]: content.externalContentId,
    [ContentDataKey.CONTENT_TYPE]: content.type,
    [ContentDataKey.CONTENT_NAME]: content.name,
    [ContentDataKey.PROVIDER_ID]: content.providerId,
    [ContentDataKey.GENRE]: content.genre,
    [ContentDataKey.LICENSE_WINDOW_START_DATE]: content.licenseWindowStartDate,
    [ContentDataKey.CHANNEL]: content.channel,

    //playlist attributes
    [ContentDataKey.PLAYLIST_ID]: content.playlistId,
    [ContentDataKey.PLAYLIST_NAME]: content.playlistName,

    //user journey attributes
    [UserJourneyDataKey.CONTAINER_POSITION]: content.containerPosition,
    [UserJourneyDataKey.CARD_POSITION]: content.cardPosition,
    [UserJourneyDataKey.TAB_ID]: content.tabId,
    [UserJourneyDataKey.TAB_NAME]: content.tabName,
    [UserJourneyDataKey.COLLECTION_ID]: content.collectionId,
    [UserJourneyDataKey.COLLECTION_NAME]: content.collectionName,
    [UserJourneyDataKey.STOREFRONT_ID]: content.storefrontId,
    [UserJourneyDataKey.STOREFRONT_NAME]: content.storefrontName,

    // Additonal attributes
    [ConvivaConstants.IS_LIVE]: content.isLive,
    [ConvivaConstants.ASSET_NAME]: content.name,
    [ConvivaConstants.DURATION]: content.duration,
    [ConvivaConstants.PLAYER_NAME]: PLAYER_NAME,
    [ConvivaConstants.STREAM_URL]: content.streamUrl,
    [ConvivaConstants.ENCODED_FRAMERATE]: content.encodedFramerate,
    [ConvivaConstants.VIEWER_ID]: content.viewerId,
    [ConvivaConstants.DEFAULT_RESOURCE]: content.defaultResource,
  };

  if (instanceOfSportContent(content)) {
    contentAttributes = {
      ...contentAttributes,
      ...toSportContentReportingData(content as SportContent),
    };
  }

  if (instanceOfTVShow(content)) {
    contentAttributes = {
      ...contentAttributes,
      ...toTVShowReportingData(content as TVShow),
    };
  }

  return contentAttributes;
}

export function toNPAWReportingData(content: Content): { [key: string]: any } {
  let contentAttributes = {
    'content.title': content.name,
    'content.type': content.type,
    'content.duration': content.duration || 0,
    'content.isLive': content?.isLive === StreamType.LIVE,
    'content.program': content.programName,
    'content.channel': content.channel,
    'content.id': content.id,
    'content.genre': content.genre,
    'content.fps': content.encodedFramerate,
    'content.playbackType': content.playbackType,
  };

  if (instanceOfNPAWTVShow(content)) {
    contentAttributes = {
      ...contentAttributes,
      ...toNPAWTVShowReportingData(content as TVShow),
    };
  }
  return contentAttributes;
}

/**
 * Builds an object with TV show custom metadata to be reported.
 * @param tvShow
 */
export function toNPAWTVShowReportingData(tvShow: TVShow): { [key: string]: any } {
  return {
    'content.season': tvShow.seasonNumber,
    'content.episodeTitle': tvShow.episodeName,
    'content.tvShow': tvShow.seriesName,
  };
}

/**
 * Builds an object with TV show custom metadata to be reported.
 * @param tvShow
 */
export function toTVShowReportingData(tvShow: TVShow): { [key: string]: any } {
  return {
    [ContentDataKey.SERIES_NAME]: tvShow.seriesName,
    [ContentDataKey.SERIES_ID]: tvShow.seriesId,
    [ContentDataKey.SEASON_NUMBER]: tvShow.seasonNumber,
    [ContentDataKey.EPISODE_NUMBER]: tvShow.episodeNumber,
  };
}

/**
 * Builds an object with sport content custom metadata to be reported.
 * @param sportContent
 */
export function toSportContentReportingData(sportContent: SportContent): { [key: string]: any } {
  return {
    ...toSportVenueReportingData(sportContent.venue),
    [ContentDataKey.SPORT_NAME]: sportContent.sportName,
    [ContentDataKey.LEAGUE_ID]: sportContent.leagueID,
    [ContentDataKey.LEAGUE_EXTERNAL_ID]: sportContent.leagueExternalID,
    [ContentDataKey.LEAGUE_NAME]: sportContent.leagueName,
    [ContentDataKey.LEAGUE_SHORT_NAME]: sportContent.leagueShortName,
    [ContentDataKey.GAME_ID]: sportContent.gameID,
    [ContentDataKey.GAME_EXTERNAL_ID]: sportContent.gameExternalID,
    [ContentDataKey.TEAM1_ID]: sportContent.team1ID,
    [ContentDataKey.TEAM1_EXTERNAL_ID]: sportContent.team1ExternalID,
    [ContentDataKey.TEAM1_NAME]: sportContent.team1Name,
    [ContentDataKey.TEAM1_SHORT_NAME]: sportContent.team1ShortName,
    [ContentDataKey.TEAM2_ID]: sportContent.team2ID,
    [ContentDataKey.TEAM2_EXTERNAL_ID]: sportContent.team2ExternalID,
    [ContentDataKey.TEAM2_NAME]: sportContent.team2Name,
    [ContentDataKey.TEAM2_SHORT_NAME]: sportContent.team2ShortName,
  };
}

/**
 * Builds an object with sport venue custom metadata to be reported.
 * @param sportVenue
 */
export function toSportVenueReportingData(sportVenue?: SportVenue): { [key: string]: any } {
  return {
    [ContentDataKey.VENUE_NAME]: sportVenue?.venueName,
    [ContentDataKey.VENUE_CITY]: sportVenue?.venueCity,
    [ContentDataKey.VENUE_COUNTRY]: sportVenue?.venueCountry,
  };
}

/**
 * Builds an object with application container custom metadata to be reported.
 * @param applicationContainer
 */
export function toApplicationContainerReportingData(applicationContainer?: ApplicationContainer): {
  [key: string]: any;
} {
  return {
    [ApplicationContainerDataKey.CONTAINER_ID]: applicationContainer?.id,
    [ApplicationContainerDataKey.CONTAINER_NAME]: applicationContainer?.name,
  };
}

/**
 * Builds an object with user error custom metadata to be reported.
 * @param userError
 */
export function toUserErrorReportingData(userError: UserError): { [key: string]: any } {
  return {
    [UserErrorDataKey.USER_ACTION_ERROR_CODE]: userError.code,
    [UserErrorDataKey.USER_ACTION_ERROR_MESSAGE]: userError.message,
    [UserErrorDataKey.USER_ACTION_ERROR_DESCRIPTION]: userError.description,
  };
}

/**
 * Builds error message from QP error.
 * @param error
 * @returns string
 */
export function toErrorMessage(error: Error): string {
  let errorMessage = `${error}`;
  if (
    error.doesBelongToCategory &&
    error.doesBelongToCategory(ErrorCodes.ERROR_CATEGORY_MASK_SERVER) &&
    error.internalError
  ) {
    errorMessage = `${error.internalError.errorMessage} (${error.internalError.errorCode})`;
  } else if (error.hexErrorCode) {
    errorMessage = `${error.errorMessage} (${error.hexErrorCode})`;
  } else if (error.errorCode !== undefined) {
    errorMessage = `${error.errorMessage} (${error.errorCode})`;
  }
  return errorMessage;
}

/**
 * Determines whether the provided Content object is Sport content or not.
 * @param content
 */
function instanceOfSportContent(content: Content): content is SportContent {
  return 'sportName' in content;
}

/**
 * Determines whether the provided Content object is a TV show or not.
 * @param content
 */
function instanceOfTVShow(content: Content): content is TVShow {
  return 'seriesId' in content;
}

/**
 * Determines whether the provided Content object is a TV show or not.
 * @param content
 */
function instanceOfNPAWTVShow(content: Content): content is TVShow {
  return 'episodeName' in content;
}

export function getConnectionType(): string {
  const connectionType = (window as any)?.navigator?.connection?.effectiveType;
  if (!connectionType) {
    return 'wifi';
  }
  switch (connectionType) {
    case 'slow-2g':
      return 'OTHER';
    case '2g':
      return '2G';
    case '3g':
      return '3G';
    case '4g':
      return '4G';
    default:
      return 'OTHER';
  }
}
export const mapKeySystemToDrmScheme = (keySystem: string) =>
  (
    [
      ['widevine', DrmScheme.WIDEVINE],
      ['playready', DrmScheme.PLAYREADY],
      ['apple', DrmScheme.FAIRPLAY],
      ['fps', DrmScheme.FAIRPLAY],
      ['clearkey', DrmScheme.CLEARKEY],
    ] as const
  ).find(([keyword]) => keySystem?.includes(keyword))?.[1] ?? undefined;

/**
 * Gets the current platform name using PlatformUtil detection methods.
 * @returns The platform name as a string
 */
export function getCurrentPlatform(): string {
  if (PlatformUtil.isTizen()) return PlatformName.TIZEN;
  if (PlatformUtil.isWebOS()) return PlatformName.LG;
  if (PlatformUtil.isXbox()) return PlatformName.XBOX;
  if (PlatformUtil.isPS4()) return PlatformName.PS4;
  if (PlatformUtil.isPS5()) return PlatformName.PS5;
  if (PlatformUtil.isVizio()) return PlatformName.VIZIO;
  if (PlatformUtil.isVIDAA()) return PlatformName.VIDAA;
  if (PlatformUtil.isPanasonicTV()) return PlatformName.PANASONIC;
  if (PlatformUtil.isWPE()) return PlatformName.COMCAST;
  if (PlatformUtil.isChromecast()) return PlatformName.CHROMECAST;

  // Check if it's a mobile browser first, then fall back to desktop
  if (PlatformUtil.isMobile()) {
    return PlatformName.WEB_MOBILE;
  }
  if (PlatformUtil.isDesktop()) {
    return PlatformName.WEB_DESKTOP;
  }

  return PlatformName.UNKNOWN;
}
